import { forkJoin, map, of, shareReplay, switchMap, type Observable } from 'rxjs';
import type { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import type { DataLibraryCollectionDataResponse } from '@core/api/workspace/models/dataLibraryCollectionDataResponse';
import type { DataLibraryCollectionItemResponse } from '@core/api/workspace/models/dataLibraryCollectionItemResponse';
import type { DataLibraryListItemResponse } from '@core/api/workspace/models/dataLibraryListItemResponse';
import { fieldOptionsToDropdownItems } from './form-field-preview.utils';
import type { BuiltForm, FormField } from '../models/built-form';

/** Answer / option shape for a collection-backed select or searchable picker. */
export type CollectionSelectOption = {
  id: string;
  uniqueKey: string;
  display: string;
  secondaryDisplay?: string;
};

/** Manual dropdown options remain label/value strings. */
export type DropdownOptionItem = { label: string; value: string };

export const SEARCHABLE_PICKER_PAGE_SIZE = 15;

export type SearchablePickerPage = {
  items: CollectionSelectOption[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  hasMore: boolean;
  uniqueFieldKey: string;
};

const collectionItemsCache = new Map<
  string,
  Observable<CollectionSelectOption[]>
>();

function cellToString(value: unknown): string {
  if (value === undefined || value === null) {
    return '';
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  return String(value);
}

export function mapCollectionItemsToSelectOptions(
  collection: Pick<
    DataLibraryCollectionDataResponse,
    'displayFieldKey' | 'uniqueFieldKey'
  >,
  items: DataLibraryCollectionItemResponse[],
  displayFieldKey: string,
  secondaryDisplayFieldKey?: string,
): CollectionSelectOption[] {
  const displayKey = displayFieldKey.trim() || collection.displayFieldKey;
  const secondaryKey = secondaryDisplayFieldKey?.trim() || '';
  const uniqueKey = collection.uniqueFieldKey;
  const seen = new Set<string>();
  const options: CollectionSelectOption[] = [];

  for (const item of items) {
    const values = item.values as Record<string, unknown>;
    const id = item.id?.trim();
    if (!id || seen.has(id)) {
      continue;
    }
    seen.add(id);
    const display = cellToString(values?.[displayKey]).trim();
    const secondaryDisplay = secondaryKey
      ? cellToString(values?.[secondaryKey]).trim()
      : '';
    const unique = cellToString(values?.[uniqueKey]).trim();
    options.push({
      id,
      uniqueKey: unique || id,
      display: display || unique || id,
      ...(secondaryDisplay ? { secondaryDisplay } : {}),
    });
  }

  return options;
}

/** Load one page of searchable picker rows (no full-list cache). */
export function fetchSearchablePickerPage(
  api: WorkspaceAPIService,
  options: {
    collectionId: string;
    primaryKey: string;
    secondaryKey: string;
    search?: string;
    pageNumber?: number;
    pageSize?: number;
    uniqueFieldKey?: string;
  },
): Observable<SearchablePickerPage> {
  const collectionId = options.collectionId.trim();
  const primaryKey = options.primaryKey.trim();
  const secondaryKey = options.secondaryKey.trim();
  const pageNumber = options.pageNumber ?? 1;
  const pageSize = options.pageSize ?? SEARCHABLE_PICKER_PAGE_SIZE;
  const search = options.search?.trim() ?? '';

  if (!collectionId || !primaryKey) {
    return of({
      items: [],
      totalCount: 0,
      pageNumber,
      pageSize,
      hasMore: false,
      uniqueFieldKey: '',
    });
  }

  const searchFields = [primaryKey, secondaryKey].filter(Boolean).join(',');

  const resolveUniqueKey$: Observable<string> = options.uniqueFieldKey?.trim()
    ? of(options.uniqueFieldKey.trim())
    : api.getDataLibraryCollection(collectionId).pipe(
        map(
          (response) =>
            response.data?.uniqueFieldKey?.trim() || primaryKey,
        ),
      );

  return resolveUniqueKey$.pipe(
    switchMap((uniqueFieldKey) =>
      api
        .listDataLibraryCollectionItems(collectionId, {
          pageNumber,
          pageSize,
          ...(search
            ? {
                search,
                searchFields,
              }
            : {}),
        })
        .pipe(
          map((response) => {
            const data = response.data;
            const rawItems = data?.items ?? [];
            const totalCount = data?.totalCount ?? 0;
            const items = mapCollectionItemsToSelectOptions(
              {
                displayFieldKey: primaryKey,
                uniqueFieldKey: uniqueFieldKey || primaryKey,
              },
              rawItems,
              primaryKey,
              secondaryKey || undefined,
            );
            return {
              items,
              totalCount,
              pageNumber: data?.pageNumber ?? pageNumber,
              pageSize: data?.pageSize ?? pageSize,
              hasMore: pageNumber * pageSize < totalCount,
              uniqueFieldKey: uniqueFieldKey || primaryKey,
            };
          }),
        ),
    ),
  );
}

/** Load collection rows as select options; cached per collection + show-as column. */
export function resolveCollectionDropdownItems(
  api: WorkspaceAPIService,
  collectionId: string,
  displayFieldKey?: string,
): Observable<CollectionSelectOption[]> {
  const id = collectionId.trim();
  if (!id) {
    return of([]);
  }

  const columnKey = displayFieldKey?.trim() ?? '';
  const cacheKey = `${id}::${columnKey}`;
  const cached = collectionItemsCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const request$ = forkJoin({
    collection: api.getDataLibraryCollection(id),
    items: api.listDataLibraryCollectionItems(id, {
      pageNumber: 1,
      pageSize: 100,
    }),
  }).pipe(
    map(({ collection, items }) => {
      const data = collection.data;
      if (!data) {
        return [];
      }
      const resolvedDisplayKey =
        columnKey || data.displayFieldKey || data.fields?.[0]?.key || '';
      if (!resolvedDisplayKey) {
        return [];
      }
      return mapCollectionItemsToSelectOptions(
        data,
        items.data?.items ?? [],
        resolvedDisplayKey,
      );
    }),
    shareReplay({ bufferSize: 1, refCount: false }),
  );

  collectionItemsCache.set(cacheKey, request$);
  return request$;
}

/** Attach collection name/key onto field refs for builder display. */
export function enrichBuiltFormCollectionRefs(
  form: BuiltForm,
  items: DataLibraryListItemResponse[],
): BuiltForm {
  const byId = new Map(
    items
      .filter((item) => item.kind === 'collection')
      .map((item) => [item.id, item] as const),
  );

  return {
    ...form,
    pages: form.pages.map((page) => ({
      ...page,
      fields: page.fields.map((field) => enrichFieldCollection(field, byId)),
    })),
  };
}

function enrichFieldCollection(
  field: FormField,
  byId: Map<string, DataLibraryListItemResponse>,
): FormField {
  const ref = field.dataLibraryCollection;
  if (!ref?.dataLibraryId) {
    return field;
  }
  const item = byId.get(ref.dataLibraryId);
  if (!item) {
    return field;
  }
  return {
    ...field,
    dataLibraryCollection: {
      ...ref,
      label: item.name,
      key: item.key,
    },
  };
}

/** Manual options or empty when the field is collection-backed (async resolve separately). */
export function resolveFieldDropdownItemsSync(
  field: FormField,
): DropdownOptionItem[] {
  if (field.dataLibraryCollection?.dataLibraryId) {
    return [];
  }
  return fieldOptionsToDropdownItems(field.options ?? []);
}

/** Clears the session cache (tests / logout). */
export function clearCollectionDropdownItemsCache(): void {
  collectionItemsCache.clear();
}
